import getWindow from 'Utils/get-window';

/**
 * Thin wrapper around Google Publisher Tag (GPT).
 *
 * The GPT library + `googletag.cmd` stub are injected into the document head
 * server-side (see template/partials/external/google-publisher-tag.js,
 * rendered by external-header.js when the WEB_ADS flag is active). Consent is
 * handled by the Sourcepoint CMP whose IAB __tcfapi/__uspapi/__gpp stubs are
 * rendered BEFORE the GPT script in the same head partial — GPT natively
 * integrates with the IAB TCF/USP APIs and holds ad requests until the CMP
 * answers, so no custom consent blocker is needed here.
 *
 * Everything below queues onto googletag.cmd, which is safe to call before
 * gpt.js has finished loading.
 */

export type AdSize = [number, number];
// GPT's 'fluid' size (a valid size token alongside [w,h] tuples) requests a
// creative that sizes itself to the slot — the shape GAM serves native
// creatives to. Native creatives are self-rendered by the page (see
// native-ad-render.ts), so no fixed height is reserved for a fluid slot.
export const FLUID = 'fluid' as const;
export type GptAdSize = AdSize | typeof FLUID;
export type SizeMappingEntry = { viewport: AdSize; sizes: AdSize[] };

export type GptSlot = {
  addService: (service: unknown) => GptSlot;
  defineSizeMapping: (sizeMapping: unknown) => GptSlot;
  setConfig: (config: {
    targeting?: Record<string, string | Array<string>>;
  }) => void;
  getSlotElementId: () => string;
};

export type GptSlotRenderEndedEvent = {
  isEmpty: boolean;
  slot: GptSlot;
};

type GptSizeMappingBuilder = {
  addSize: (viewport: AdSize, sizes: AdSize[]) => GptSizeMappingBuilder;
  build: () => unknown;
};

type GptPubAdsService = {
  clearTargeting: (key?: string) => void;
  setPublisherProvidedId: (ppid: string) => void;
  addEventListener: (
    eventName: string,
    listener: (event: GptSlotRenderEndedEvent) => void,
  ) => void;
  removeEventListener: (
    eventName: string,
    listener: (event: GptSlotRenderEndedEvent) => void,
  ) => void;
};

/**
 * A GPT secure signal provider (encrypted signals to AdX open-auction
 * bidders): GPT invokes collectorFunction asynchronously, caches the
 * resolved signal, and attaches it to ad requests — a slow or rejected
 * collector never delays ad serving.
 * https://developers.google.com/publisher-tag/reference#googletag.secureSignals
 */
export type SecureSignalProvider = {
  id: string;
  collectorFunction: () => Promise<string>;
};

type Gpt = {
  cmd: Array<() => void>;
  setConfig: (config: {
    targeting?: Record<string, string | null> | null;
  }) => void;
  defineSlot: (
    adUnitPath: string,
    sizes: GptAdSize[],
    divId: string,
  ) => GptSlot | null;
  destroySlots: (slots?: GptSlot[]) => boolean;
  display: (divId: string) => void;
  enableServices: () => void;
  sizeMapping: () => GptSizeMappingBuilder;
  pubads: () => GptPubAdsService;
  // Populated by gpt.js on load; publishers may initialize it to a plain
  // array beforehand (the documented pattern) — gpt.js drains it.
  secureSignalProviders?: SecureSignalProvider[];
};

const getGpt = (): Gpt | undefined =>
  getWindow().googletag as unknown as Gpt | undefined;

/**
 * Enqueue a function onto GPT. Returns false when the GPT stub is not on the
 * page at all (ads disabled, non-production, adblock stripped the script).
 */
export const gptCmdPush = (fn: () => void): boolean => {
  const gpt = getGpt();
  if (!gpt || !gpt.cmd) {
    return false;
  }
  gpt.cmd.push(fn);
  return true;
};

// enableServices() must only be coordinated once per page lifetime; slots
// defined afterwards are still served on display().
let servicesEnabled = false;

// Monotonic counter so every rendered slot gets a unique div id, including
// repeated in-feed slots and SPA remounts. Only ever used client-side (ids
// are assigned on mount), so server module state cannot leak across requests.
let adDivCounter = 0;
export const nextAdDivId = (name: string): string =>
  `fl-ad-${name}-${++adDivCounter}`;

export type AdSlotHandle = {
  destroy: () => void;
};

export type DefineAdSlotOptions = {
  adUnitPath: string;
  // Fixed sizes ([w,h] tuples) for display slots, or [FLUID] for a
  // self-rendered native slot.
  sizes: GptAdSize[];
  divId: string;
  sizeMapping?: SizeMappingEntry[];
  targeting?: Record<string, string | Array<string>>;
  onSlotRenderEnded?: (event: GptSlotRenderEndedEvent) => void;
};

/**
 * Define + display a GPT slot. Returns a handle whose destroy() must be
 * called on unmount (destroySlots + listener cleanup), or null when GPT is
 * not present on the page.
 */
export const defineAndDisplayAdSlot = (
  options: DefineAdSlotOptions,
): AdSlotHandle | null => {
  let slot: GptSlot | null = null;
  let renderEndedListener: ((event: GptSlotRenderEndedEvent) => void) | null =
    null;

  const queued = gptCmdPush(() => {
    const gpt = getGpt();
    if (!gpt) {
      return;
    }
    slot = gpt.defineSlot(options.adUnitPath, options.sizes, options.divId);
    // defineSlot returns null e.g. for a duplicate div id
    if (!slot) {
      return;
    }
    slot.addService(gpt.pubads());
    if (options.sizeMapping && options.sizeMapping.length > 0) {
      const builder = options.sizeMapping.reduce(
        (mapping, { viewport, sizes }) => mapping.addSize(viewport, sizes),
        gpt.sizeMapping(),
      );
      slot.defineSizeMapping(builder.build());
    }
    if (options.targeting) {
      slot.setConfig({ targeting: options.targeting });
    }
    if (options.onSlotRenderEnded) {
      renderEndedListener = (event: GptSlotRenderEndedEvent) => {
        if (event.slot === slot && options.onSlotRenderEnded) {
          options.onSlotRenderEnded(event);
        }
      };
      gpt.pubads().addEventListener('slotRenderEnded', renderEndedListener);
    }
    if (!servicesEnabled) {
      gpt.enableServices();
      servicesEnabled = true;
    }
    gpt.display(options.divId);
  });

  if (!queued) {
    return null;
  }

  return {
    destroy: () => {
      gptCmdPush(() => {
        const gpt = getGpt();
        if (!gpt) {
          return;
        }
        if (renderEndedListener) {
          gpt
            .pubads()
            .removeEventListener('slotRenderEnded', renderEndedListener);
          renderEndedListener = null;
        }
        if (slot) {
          gpt.destroySlots([slot]);
          slot = null;
        }
      });
    },
  };
};

/**
 * Set the GPT Publisher Provided ID (PPID) for this page. Must be enqueued
 * before the first defineSlot/refresh cmd for the first ad request to carry
 * it — googletag.cmd preserves ordering, so calling this before the slot is
 * defined is sufficient even while gpt.js is still loading. Returns false
 * when the GPT stub is not on the page.
 */
export const setPublisherProvidedId = (ppid: string): boolean =>
  gptCmdPush(() => {
    const gpt = getGpt();
    if (!gpt) {
      return;
    }
    gpt.pubads().setPublisherProvidedId(ppid);
  });

/**
 * Register a GPT secure signal provider (e.g. the LiveRamp envelope under
 * id 'liveramp.com'). Pushed directly onto googletag.secureSignalProviders
 * (initialized to an array when gpt.js has not loaded yet — the documented
 * pattern; gpt.js adopts the array on load). GPT resolves the collector
 * asynchronously and never blocks ad requests on it. Returns false when the
 * GPT stub is not on the page.
 */
export const registerSecureSignalProvider = (
  provider: SecureSignalProvider,
): boolean => {
  const gpt = getGpt();
  if (!gpt || !gpt.cmd) {
    return false;
  }
  gpt.secureSignalProviders = gpt.secureSignalProviders || [];
  gpt.secureSignalProviders.push(provider);
  return true;
};

/**
 * Page-level key-values for reporting / floors (e.g. page_type, topic_slug).
 * Null/undefined values clear the key.
 */
export const setPageTargeting = (
  values: Record<string, string | null | undefined>,
): void => {
  gptCmdPush(() => {
    const gpt = getGpt();
    if (!gpt) {
      return;
    }
    // googletag.setConfig replaces the deprecated PubAdsService.setTargeting;
    // a null value clears the key (replacing clearTargeting).
    const targeting: Record<string, string | null> = {};
    Object.entries(values).forEach(([key, value]) => {
      targeting[key] = value || null;
    });
    gpt.setConfig({ targeting });
  });
};
